import { useApiError } from '@/errorHandling/context/ApiErrorContext';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import type React from 'react';
import { useEffect, useState } from 'react';
import ErrorFallback from '../ErrorFallback';

type ApiErrorBoundaryProps = {
  children: React.ReactNode;
};

const RequiredRedirectApiError = [
  'UNAUTHORIZED',
  'AGREEMENT_REQUIRED',
  'USER_ENROLLMENT_NOT_SELECTED',
] as const;

type RequiredRedirectApiErrorType = (typeof RequiredRedirectApiError)[number];

export const ApiErrorBoundary: React.FC<ApiErrorBoundaryProps> = ({
  children,
}) => {
  const { apiError, setApiError } = useApiError();
  const router = useRouterState();
  const navigate = useNavigate();
  const [isRedirectChecked, setIsRedirectChecked] = useState(false);

  const hasRequireRedirectError =
    apiError &&
    RequiredRedirectApiError.includes(
      apiError.errorType as RequiredRedirectApiErrorType,
    );

  useEffect(() => {
    if (!hasRequireRedirectError) return;

    switch (apiError.errorType) {
      case 'UNAUTHORIZED':
        navigate({
          to: '/signin',
          replace: true,
        });
        break;
      case 'AGREEMENT_REQUIRED':
        if (router.location.pathname === '/terms-of-service') break;

        navigate({
          search: {
            redirectTo: router.location.pathname,
          },
          to: '/terms-of-service',
          replace: true,
        });
        break;
      case 'USER_ENROLLMENT_NOT_SELECTED':
        if (router.location.pathname === '/select-user-enrollment') break;

        navigate({
          to: '/select-user-enrollment',
          search: {
            disableAutoSelect: false,
          },
          replace: true,
        });
        break;
    }

    // ここに来るのはリダイレクトが必要なエラーのみなので、エラーをクリアしてリダイレクト済みフラグを立てる
    setApiError(null);
    setIsRedirectChecked(true);
  }, [
    apiError,
    setApiError,
    navigate,
    router.location.pathname,
    hasRequireRedirectError,
  ]);

  // リダイレクトの際はchildrenを返さないとその先で描画されない
  if (isRedirectChecked) {
    return children;
  }

  // エラーページ表示は以下で分岐
  if (apiError) {
    switch (apiError.errorType) {
      case 'NOT_FOUND':
        return <ErrorFallback type={'not_found'} />;
      case 'MAINTENANCE':
        return (
          <ErrorFallback type={'maintenance'} message={apiError.message} />
        );
      default:
        return <ErrorFallback type={'normal'} />;
    }
  }

  return children;
};
